import { useCallback, useEffect, useMemo, useState } from "react";

import {
  tradeLogApi,
  api as accountsApi,
  type ManualTrade,
  type ManualTradeInput,
  type Account,
  ApiError,
} from "../lib/api";
import TradeLogForm from "../components/TradeLogForm";
import Modal from "../components/Modal";

type Mode =
  | { kind: "closed" }
  | { kind: "create" }
  | { kind: "edit"; trade: ManualTrade };

export default function TradeLog() {
  const [trades, setTrades] = useState<ManualTrade[] | null>(null);
  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>({ kind: "closed" });
  const [busyId, setBusyId] = useState<string | null>(null);
  const [filterAccount, setFilterAccount] = useState<string>(""); // "" → all

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [t, a] = await Promise.all([tradeLogApi.list(), accountsApi.list()]);
      setTrades(t);
      setAccounts(a);
    } catch (e) {
      setError(messageFrom(e));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const accountLookup = useMemo(() => {
    const map = new Map<string, Account>();
    (accounts ?? []).forEach((a) => map.set(a.account_id, a));
    return map;
  }, [accounts]);

  const visible = useMemo(() => {
    if (!trades) return null;
    const filtered = filterAccount ? trades.filter((t) => t.account_id === filterAccount) : trades;
    return [...filtered].sort((a, b) => (a.entry_date < b.entry_date ? 1 : -1));
  }, [trades, filterAccount]);

  async function handleCreate(input: ManualTradeInput) {
    await tradeLogApi.create(input);
    setMode({ kind: "closed" });
    await refresh();
  }

  async function handleUpdate(id: string, input: ManualTradeInput) {
    // Send everything as a patch — fields the user left untouched are still
    // valid values; the server uses minProperties: 1 which is satisfied.
    await tradeLogApi.update(id, input);
    setMode({ kind: "closed" });
    await refresh();
  }

  async function handleDelete(id: string) {
    if (!confirm("Delete this trade entry? This cannot be undone.")) return;
    setBusyId(id);
    try {
      await tradeLogApi.remove(id);
      await refresh();
    } catch (e) {
      setError(messageFrom(e));
    } finally {
      setBusyId(null);
    }
  }

  const sortedAccounts = useMemo(() => {
    return [...(accounts ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  }, [accounts]);

  return (
    <section className="accounts">
      <header className="accounts-header">
        <h1>Trade log</h1>
        <div className="header-actions">
          <label className="inline-checkbox">
            <span>Account:</span>
            <select
              value={filterAccount}
              onChange={(e) => setFilterAccount(e.target.value)}
            >
              <option value="">All accounts</option>
              {sortedAccounts.map((a) => (
                <option key={a.account_id} value={a.account_id}>
                  {a.name} ({a.account_number})
                </option>
              ))}
            </select>
          </label>
          <button className="primary" onClick={() => setMode({ kind: "create" })}>
            + New trade
          </button>
        </div>
      </header>

      {error && <div className="error">{error}</div>}

      {visible === null ? (
        <p>Loading&hellip;</p>
      ) : visible.length === 0 ? (
        <p className="empty">
          No trades{filterAccount ? " for this account" : ""} yet. Click{" "}
          <strong>+ New trade</strong> to add one.
        </p>
      ) : (
        <table className="accounts-table">
          <thead>
            <tr>
              <th>Entry date</th>
              <th>Firm</th>
              <th>Account</th>
              <th>Instrument</th>
              <th>Strategy</th>
              <th>Side</th>
              <th className="num">Size</th>
              <th className="num">Entry price</th>
              <th className="num">SL</th>
              <th className="num">TP</th>
              <th className="num">P/L</th>
              <th>Notes</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {visible.map((t) => {
              const account = accountLookup.get(t.account_id);
              return (
                <tr key={t.trade_id}>
                  <td>{formatDateTime(t.entry_date)}</td>
                  <td>{t.prop_firm}</td>
                  <td>
                    {account ? (
                      <>
                        {account.name}{" "}
                        <span className="mono muted">({account.account_number})</span>
                      </>
                    ) : (
                      <span className="muted">(deleted)</span>
                    )}
                  </td>
                  <td>{t.instrument}</td>
                  <td>{t.strategy ? t.strategy : <span className="muted">—</span>}</td>
                  <td>{t.side}</td>
                  <td className="num">{formatNumber(t.size)}</td>
                  <td className="num">{formatNumber(t.entry_price)}</td>
                  <td className="num">{t.stop_loss != null ? formatNumber(t.stop_loss) : <span className="muted">—</span>}</td>
                  <td className="num">{t.target_profit != null ? formatNumber(t.target_profit) : <span className="muted">—</span>}</td>
                  <td className={`num ${t.pnl > 0 ? "pnl-pos" : t.pnl < 0 ? "pnl-neg" : ""}`}>
                    {formatPnl(t.pnl)}
                  </td>
                  <td className="notes-cell">
                    {t.notes ? t.notes : <span className="muted">—</span>}
                  </td>
                  <td className="row-actions">
                    <button
                      className="link-button"
                      onClick={() => setMode({ kind: "edit", trade: t })}
                    >
                      Edit
                    </button>
                    <button
                      className="link-button danger"
                      disabled={busyId === t.trade_id}
                      onClick={() => handleDelete(t.trade_id)}
                    >
                      {busyId === t.trade_id ? "…" : "Delete"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <Modal open={mode.kind !== "closed"} onClose={() => setMode({ kind: "closed" })}>
        {mode.kind === "create" && (
          <TradeLogForm
            title="New trade"
            onCancel={() => setMode({ kind: "closed" })}
            onSubmit={handleCreate}
          />
        )}
        {mode.kind === "edit" && (
          <TradeLogForm
            title="Edit trade"
            initial={mode.trade}
            onCancel={() => setMode({ kind: "closed" })}
            onSubmit={(input) => handleUpdate(mode.trade.trade_id, input)}
          />
        )}
      </Modal>
    </section>
  );
}

function formatDateTime(iso: string): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function formatNumber(n: number): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

function formatPnl(n: number): string {
  const sign = n > 0 ? "+" : "";
  return sign + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function messageFrom(e: unknown): string {
  if (e instanceof ApiError) return `${e.message} (HTTP ${e.status})`;
  if (e instanceof Error) return e.message;
  return String(e);
}
